import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router";

import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H1, Subheader } from "simplydo/react/ui/header";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";

import type {
  VerifyEmailApiResponse,
  VerifyEmailResendResponse,
} from "./types";

const CompleteVerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [attemptedRetry, setAttemptedRetry] = useState(false);
  const [emails, setEmails] = useState<OpenAPI.Schemas["User"]["emails"]>([]);
  const [emailVerified, setEmailVerified] = useState(false);

  const onEmailsUpdate = useCallback(
    (updatedEmails: OpenAPI.Schemas["User"]["emails"]) =>
      dispatch(actions.user.updateEmails(updatedEmails)),
    [dispatch],
  );
  const userId = user?._id;
  // Captured on load so verifying here doesn't re-trigger the redirect below
  const [userAlreadyVerified] = useState(() => !!user?.emails?.[0]?.verified);

  const token = useMemo(() => {
    const params = new SearchParams(location.search);
    return params.get("token");
  }, [location.search]);

  useEffect(() => {
    // No need to check the token if we already know the email is verified
    if (userAlreadyVerified) {
      navigate("/");
      toast("Your email is already verified");
      return;
    }

    let isMounted = true;

    setLoading(true);
    api.auth.verifyEmail(
      token ?? "",
      (({ success, attemptedRetry, emails }: VerifyEmailApiResponse) => {
        if (!isMounted) return;

        setEmails(emails ?? []);
        if (success) {
          toast.success("Email address verified successfully");
          setEmailVerified(true);
          if (userId) {
            onEmailsUpdate(emails ?? []);
            navigate("/");
          }
        } else {
          setAttemptedRetry(attemptedRetry ?? false);
        }
        setLoading(false);
      }) as () => void,
      (err) => {
        if (!isMounted) return;

        setLoading(false);
        if (err.message === "This email is already verified") {
          navigate("/");
          toast("Your email is already verified");
        } else {
          setErrorMessage(err.message);
        }
      },
    );
    return () => {
      isMounted = false;
    };
  }, [userId, userAlreadyVerified, token, onEmailsUpdate, navigate]);

  return (
    <div>
      <H1>Email verification</H1>
      <Divider />
      {loading ? (
        <Subheader>We are verifying your email address...</Subheader>
      ) : null}
      {emailVerified ? (
        <Message color="blue">
          <MessageHeader>Email verified</MessageHeader>
          <p>
            Your email address has been verified successfully. You can now close
            this tab.
          </p>
        </Message>
      ) : null}
      {loading && <Loader />}
      {errorMessage ? <Message color="red">{errorMessage}</Message> : null}

      {!loading && !emailVerified ? (
        <>
          <Message color="orange">
            This email verification link has expired. Email verifications are
            only valid for one day.
          </Message>
          {attemptedRetry ? (
            <Message color="green">
              <p>We have sent you a new link to:</p>
              <p>{(emails ?? user?.emails ?? [])[0]?.address}</p>
              <p>You can close this tab now.</p>
            </Message>
          ) : (
            <Button
              onClick={() => {
                setErrorMessage("");
                setAttemptedRetry(true);
                setLoading(true);
                api.auth.requestEmailVerification(
                  token ?? "",
                  (({ emails }: VerifyEmailResendResponse) => {
                    setEmails(emails ?? []);
                    setLoading(false);
                  }) as () => void,
                  (err) => {
                    setLoading(false);
                    setAttemptedRetry(false);
                    setErrorMessage(err.message);
                    if (err.message === "This email is already verified") {
                      navigate("/");
                      toast("Your email is already verified");
                    }
                  },
                );
              }}
              color="primary"
            >
              Re-send verification
            </Button>
          )}
        </>
      ) : null}
      {!loading ? (
        <>
          <Divider />
          <Button onClick={() => navigate("/")}>
            {user ? "Go to homepage" : "Go to login"}
          </Button>
        </>
      ) : null}
    </div>
  );
};

export default CompleteVerifyEmail;
